import { IPagination, IPageInfo } from "./pagination.d";
/**
 * Represents a pagination configuration.
 */
export class Pagination implements IPagination {
  constructor(data?: IPagination) {
    if (data) {
      this.fromObject(data);
    }
  }

  /**
   * The current page number.
   */
  page_number: number = 1;

  /**
   * The number of items per page.
   */
  page_size: number = 10;

  /**
   * The field to order the items by.
   */
  order_by: string = "id";

  /**
   * Specifies whether the items should be ordered in ascending order.
   */
  asc: boolean = false;

  /**
   * Specifies whether there is a next page available.
   */
  has_next_page: boolean = false;

  /**
   * Specifies whether there is a previous page available.
   */
  has_previous_page: boolean = false;

  /// //////////////////////////////////////////////
  /// ////////////// METHODS ///////////////////////
  /// //////////////////////////////////////////////

  /**
   * Converts the pagination configuration to a PageInfo object.
   * @returns The PageInfo object representing the pagination configuration.
   */
  toPageInfo(): IPageInfo {
    return new PageInfo().fromPagination(this);
  }

  /**
   * Updates the pagination configuration based on the provided PageInfo object.
   * @param info - The PageInfo object containing the new pagination configuration.
   * @returns The updated Pagination object.
   */
  fromPageInfo(info: IPageInfo): Pagination {
    this.page_number = info.pageNumber;
    this.page_size = info.pageSize;
    this.order_by = info.orderBy;
    this.asc = !info.orderDesc;
    this.has_next_page = info.hasNextPage;
    this.has_previous_page = info.hasPreviousPage;

    return this;
  }

  /**
   * Converts an object of type IPagination to a Pagination instance.
   *
   * @param obj - The object to convert.
   * @returns The converted Pagination instance.
   */
  fromObject(obj: IPagination): Pagination {
    this.page_number = obj.page_number;
    this.page_size = obj.page_size;
    this.order_by = obj.order_by;
    this.asc = !!obj.asc;
    this.has_next_page = obj.has_next_page ?? false;
    this.has_previous_page = obj.has_previous_page ?? false;

    return this;
  }

  /**
   * Converts a incomplete pagination object to defaults
   */
  withDefaults({
    page_number = 1,
    page_size = 10,
    order_by = "id",
    asc = false,
  }): Pagination {
    this.page_number = page_number;
    this.page_size = page_size;
    this.order_by = order_by;
    this.asc = asc;
    this.has_next_page = false;
    this.has_previous_page = false;

    return this;
  }

  /**
   * Converts the pagination configuration to a cache key.
   * @returns The cache key representing the pagination configuration.
   */
  toCacheKey(): string {
    return `${this.page_number}-${this.page_size}-${this.order_by}-${this.asc}`;
  }

  /**
   * Converts the pagination configuration to a route query.
   * @returns The route query representing the pagination configuration.
   */
  toRouteQuery(): string {
    return JSON.stringify({
      page_number: this.page_number,
      page_size: this.page_size,
      order_by: this.order_by,
      asc: this.asc,
    });
  }

  fromRouteQuery(query: string): Pagination {
    const parsed = JSON.parse(query);
    return this.fromObject(parsed);
  }

  equals(pag: IPagination): boolean {
    return (
      this.page_number === pag.page_number &&
      this.page_size === pag.page_size &&
      this.order_by === pag.order_by &&
      this.asc === pag.asc
    );
  }

  deepCopy(): Pagination {
    return new Pagination().fromObject(this);
  }

  /// //////////////////////////////////////////////
  /// ////////////// STATIC METHODS ////////////////
  /// //////////////////////////////////////////////

  /**
   * Creates a Pagination instance from a PageInfo object.
   * @param info - The PageInfo object containing the pagination configuration.
   * @returns The created Pagination instance.
   */
  static FromPageInfo(info: IPageInfo): Pagination {
    return new Pagination().fromPageInfo(info);
  }

  /**
   * Creates a Pagination instance from an IPagination object.
   * @param obj - The IPagination object containing the pagination configuration.
   * @returns The created Pagination instance.
   */
  static FromObject(obj: IPagination): Pagination {
    return new Pagination().fromObject(obj);
  }

  /**
   * Converts a pagination configuration to a cache key.
   * @param pag - The pagination configuration to convert.
   * @returns The cache key representing the pagination configuration.
   */
  static ToCacheKey(pag: IPagination): string {
    return `${pag?.page_number ?? 1}-${pag?.page_size ?? 10}-${pag?.order_by ?? "id"}-${pag?.asc ?? false}`;
  }

  /**
   * Creates a Pagination instance from a route query.
   * @param query - The route query containing the pagination configuration.
   * @returns The created Pagination instance.
   */
  static FromRouteQuery(query: string): Pagination {
    const parsed = JSON.parse(query);
    return new Pagination().fromObject(parsed);
  }

  /**
   * Creates a Pagination instance with defaults
   */
  static WithDefaults({
    page_number = 1,
    page_size = 10,
    order_by = "id",
    asc = false,
  }): Pagination {
    return new Pagination().withDefaults({
      page_number,
      page_size,
      order_by,
      asc,
    });
  }
}

/**
 * Represents a page information configuration.
 */
export class PageInfo implements IPageInfo {
  /// ////////////////////////////////////////////////
  /// ////////////// PUBLIC VARIABLES ////////////////
  /// ////////////////////////////////////////////////
  /**
   * Specifies whether there is a next page available.
   */
  hasNextPage: boolean = false;

  /**
   * Specifies whether there is a previous page available.
   */
  hasPreviousPage: boolean = false;

  /**
   * The current page number.
   */
  pageNumber: number = 1;

  /**
   * The number of items per page.
   */
  pageSize: number = 10;

  /**
   * The field to order the items by.
   */
  orderBy: string = "id";

  /**
   * Specifies whether the items should be ordered in descending order.
   */
  orderDesc: boolean = false;

  /// ///////////////////////////////////////
  /// ////////////// METHODS ////////////////
  /// ///////////////////////////////////////

  /**
   * Converts the page information configuration to a Pagination object.
   * @returns The Pagination object representing the page information configuration.
   */
  toPagination(): Pagination {
    return new Pagination().fromPageInfo(this);
  }

  /**
   * Updates the page information configuration based on the provided Pagination object.
   * @param pagination - The Pagination object containing the new page information configuration.
   * @returns The updated PageInfo object.
   */
  fromPagination(pagination: IPagination): PageInfo {
    this.hasNextPage = pagination.has_next_page;
    this.hasPreviousPage = pagination.has_previous_page;
    this.pageNumber = pagination.page_number;
    this.pageSize = pagination.page_size;
    this.orderBy = pagination.order_by;
    this.orderDesc = !pagination.asc;

    return this;
  }

  /**
   * Converts an object of type IPageInfo to a PageInfo instance.
   *
   * @param obj - The object to convert.
   * @returns The converted PageInfo instance.
   */
  fromObject(obj: IPageInfo): PageInfo {
    this.hasNextPage = obj.hasNextPage;
    this.hasPreviousPage = obj.hasPreviousPage;
    this.pageNumber = obj.pageNumber;
    this.pageSize = obj.pageSize;
    this.orderBy = obj.orderBy;
    this.orderDesc = obj.orderDesc;

    return this;
  }

  /**
   * Converts the page information configuration to a cache key.
   * @returns The cache key representing the page information configuration.
   */
  toQueryParams(): string {
    return JSON.stringify({
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      orderBy: this.orderBy,
      orderDesc: this.orderDesc,
    });
  }

  /// //////////////////////////////////////////////
  /// ////////////// STATIC METHODS ////////////////
  /// //////////////////////////////////////////////

  static FromPagination(info: IPagination): IPageInfo {
    return new PageInfo().fromPagination(info);
  }

  static FromObject(obj: IPageInfo): PageInfo {
    return new PageInfo().fromObject(obj);
  }
}
